
import { cancelBetCL } from '@/api'
import { useStore } from '@/store'
import { AppMutation } from '@/store/types'
import { Dictionary } from '@/types/dictionary'
import { BetType, EventBuss } from '@/types/global'
import { abbreviateNumber, getDeviceOrientation } from '@/utils'
import {
    BetItem,
    ChipOption,
    computed,
    defineComponent,
    Emitter,
    inject,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    PropType,
    ref,
    StyleValue,
    TableData,
    watch
} from 'vue'
import { useI18n } from 'vue-i18n'

export default defineComponent({
    emits: ['place-bet', 'cancel-bet', 'on-error'],
    props: {
        label: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        ratio: {
            required: false,
            default: '',
            type: String as PropType<string>
        },
        color: {
            type: Number as PropType<number>,
            required: true
        },
        confirmedBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        tempBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        totalBet: {
            type: Number as PropType<number>,
            required: false,
            default: 0
        },
        betType: {
            type: Number as PropType<number>,
            require: true
        },
        width: {
            type: Number as PropType<number>,
            required: true
        },
        height: {
            type: Number as PropType<number>,
            required: true
        },
        mWidth: {
            type: Number as PropType<number>,
            required: true
        },
        mHeight: {
            type: Number as PropType<number>,
            required: true
        },
        mData: {
            type: Object as PropType<TableData>,
            require: false
        },
        chipPrefix: {
            type: String as PropType<'--pc' | '--mp'>,
            default: () => '--pc'
        },
        isFreeBaccarat: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false
        },
        isGrayMode: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        allowBet: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        showFlashingBg: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        errorMessage: {
            type: String as PropType<string>,
            required: false,
            default: ''
        }
    },
    setup(props, { emit }) {
        const { t } = useI18n()
        const emitter = inject('emitter') as Emitter
        const store = useStore()
        const chipsUI = ref<
            {
                x: number
                y: number
                amount: number
                class: string
                type: 'temp' | 'confirmed'
            }[]
        >([])

        onMounted(() => {
            emitter.on(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        onBeforeUnmount(() => {
            emitter.off(EventBuss.PLACE_CHIP, handleOnPlaceChip)
        })

        const betColor = computed(() => {
            const { betType } = props

            let result = ''

            switch (betType) {
                case BetType.BANKER:
                case BetType.DRAGON:
                case BetType.BANKER_PAIR:
                case BetType.BANKER_SUPER6:
                case BetType.DRAGON_7:
                    result = 'red'
                    break
                case BetType.PLAYER:
                case BetType.TIGER:
                case BetType.PLAYER_PAIR:
                case BetType.BEAR_8:
                    result = 'blue'
                    break
                case BetType.BC_TIE:
                case BetType.DT_TIE:
                    result = 'green'
                    break
                case BetType.SUPER6:
                case BetType.SMALL_TIGER:
                case BetType.BIG_TIGER:
                    result = 'super6'
                    break
            }

            // if (color === 1) return 'red'
            // else if (color === 2) return 'green'
            // else if (color === 3) return 'blue'

            if (props.isGrayMode) return 'gray'
            return result
        })

        const sumOfTempBetandConfirmedBet = computed(() => {
            const confirmedBet = props.confirmedBet ?? 0
            const tempBet = props.tempBet ?? 0
            return confirmedBet + tempBet
        })

        const toShortValue = (value: number) => {
            return abbreviateNumber(value, lang.value)
        }

        const placeBet = () => {
            if (props.isGrayMode && props.allowBet) {
                let error = props.errorMessage

                if (!error) {
                    if (props.betType === BetType.DRAGON_7) {
                        error = t('prompts.d7error')
                    } else if (props.betType === BetType.BEAR_8) {
                        error = t('prompts.b8error')
                    } else if (props.betType === BetType.BIG_TIGER) {
                        error = t('prompts.bigtigererror')
                    } else if (props.betType === BetType.SMALL_TIGER) {
                        error = t('prompts.smalltigererror')
                    } else if (props.betType === BetType.SUPER6) {
                        error = t('prompts.super6tigermutual')
                    }

                }

                if (error) emit('on-error', error)
                return
            }

            if (!props.allowBet && props.isGrayMode) {
                return
            }

            emit('place-bet', props.betType)
        }

        const removeTempUIBet = () => {
            chipsUI.value = chipsUI.value.filter((i) => i.type !== 'temp')
        }

        const applyTempUIBet = () => {
            chipsUI.value = chipsUI.value.map((i) => {
                if (i.type === 'temp') i.type = 'confirmed'
                return i
            })
        }

        const cancelBet = () => {
            // console.log('cancel Bet', props.betType)

            if (props.mData) {
                const params = {
                    memberId: memberId.value,
                    tableNo: props.mData.tableNo,
                    gameNo: props.mData.expand.gameNo,
                    playNum: '',
                    token: token.value
                }

                const betType = props.betType
                const betTypes: number[] = []
                if (betType === BetType.BANKER && props.isFreeBaccarat) {
                    params.playNum = '1-20-201'
                    betTypes.push(BetType.BANKER)
                }
                else if (betType === BetType.BANKER && !props.isFreeBaccarat) {
                    params.playNum = '1-14-141'
                    betTypes.push(BetType.BANKER)
                } else if (betType === BetType.PLAYER) {
                    params.playNum = '1-15-151'
                    betTypes.push(BetType.PLAYER)
                } else if (betType === BetType.BC_TIE) {
                    params.playNum = '1-16-161'
                    betTypes.push(BetType.BC_TIE)
                } else if (betType === BetType.PLAYER_PAIR) {
                    params.playNum = '1-15-152'
                    betTypes.push(BetType.PLAYER_PAIR)
                } else if (betType === BetType.BANKER_PAIR) {
                    params.playNum = '1-14-142'
                    betTypes.push(BetType.BANKER_PAIR)
                } else if (betType === BetType.DRAGON) {
                    params.playNum = '1-17-171'
                    betTypes.push(BetType.DRAGON)
                } else if (betType === BetType.TIGER) {
                    params.playNum = '1-18-181'
                    betTypes.push(BetType.TIGER)
                } else if (betType === BetType.DT_TIE) {
                    params.playNum = '1-19-191'
                    betTypes.push(BetType.DT_TIE)
                } else if (betType === BetType.DRAGON_7) {
                    params.playNum = '1-22-221'
                    betTypes.push(BetType.DRAGON_7)
                } else if (betType === BetType.BEAR_8) {
                    params.playNum = '1-23-231'
                    betTypes.push(BetType.BEAR_8)
                } else if (betType === BetType.BIG_TIGER) {
                    params.playNum = '1-24-241'
                    betTypes.push(BetType.BIG_TIGER)
                } else if (betType === BetType.SMALL_TIGER) {
                    params.playNum = '1-25-251'
                    betTypes.push(BetType.SMALL_TIGER)
                } else if (betType === BetType.SUPER6) {
                    params.playNum = '1-21-211'
                    betTypes.push(BetType.SUPER6)
                }

                // super6  1-21-211
                // 龙七 | dragon7  1-22-221
                // 熊八 | bear8  1-23-231
                // 大老虎 | big tiger  1-24-241
                // 小老虎 | small tiger  1-25-251




                store.commit(AppMutation.SHOW_LOADING)
                cancelBetCL(params)
                    .then((response) => {
                        store.commit(AppMutation.HIDE_LOADING)

                        if (response.data.status === 200) {
                            if (props.mData) {
                                const mKey = props.mData.expand.gameNo
                                const prevItem = prevBets.value.Item(mKey)

                                if (prevItem) {
                                    const remains = prevItem.filter(
                                        (x) => betTypes.indexOf(x.type) === -1
                                    )

                                    if (remains.length > 0) {
                                        store.commit(AppMutation.SET_LAST_BET, {
                                            key: mKey,
                                            values: remains
                                        })
                                        // console.log('not deleted')
                                    } else {
                                        store.commit(
                                            AppMutation.REMOVE_LAST_BET,
                                            mKey
                                        )

                                        // console.log('Deleted')
                                    }
                                }
                            }

                            emit('cancel-bet', props.betType)
                            resetBetUI()
                        } else if (response.data.status === 100) {
                            emitter.emit(
                                EventBuss.SMALL_TOAST,
                                response.data.description
                            )
                        }
                    })
                    .catch(() => {
                        store.commit(AppMutation.HIDE_LOADING)
                        emitter.emit(
                            EventBuss.SMALL_TOAST,
                            t('prompts.networkerror')
                        )
                    })
            }
        }

        const handleOnPlaceChip = (arg: {
            t: number
            amt: number
            type: 'temp' | 'confirmed'
        }) => {
            if (arg.t !== props.betType) return

            if (arg.type === 'confirmed') {
                const sum = chipsUI.value.reduce((s, c) => s + c.amount, 0)
                if (chipsUI.value.length > 0 && sum === arg.amt) {
                    // Keep existing chip pieces (e.g. after applyTempUIBet)
                    chipsUI.value = chipsUI.value.map((i) => ({
                        ...i,
                        type: 'confirmed' as const
                    }))
                    return
                }
                chipsUI.value = []
            }

            // Same dimensions as chipContainerStyle
            const isLandscape = deviceOrientation.value === 'landscape'
            const w = Math.abs(isLandscape ? props.width : props.mWidth)
            const h = Math.abs(isLandscape ? props.height : props.mHeight)

            const chipSize = props.chipPrefix === '--mp' ? 30 : 20
            const chipHalf = chipSize / 2

            // Keep chip fully inside (x/y are centers due to translate(-50%, -50%))
            const minX = chipHalf
            const maxX = Math.max(chipHalf, w - chipHalf)
            const minY = chipHalf
            const maxY = Math.max(chipHalf, h - chipHalf)

            let startX = Math.floor(w / 2)
            let startY = Math.floor(h / 2)

            if (chipsUI.value.length > 0) {
                const lastItem = chipsUI.value[chipsUI.value.length - 1]
                startX = lastItem.x
                startY = lastItem.y
            }

            const clamp = (v: number, min: number, max: number) =>
                Math.min(Math.max(v, min), max)

            // Prefer near previous chip, but never leave the box
            let _x = getRandomPosition(minX, maxX)
            let tries = 0
            while (Math.abs(_x - startX) > chipSize && tries < 20) {
                _x = clamp(getRandomPosition(minX, maxX), minX, maxX)
                tries++
            }
            _x = clamp(_x, minX, maxX)

            let _y = getRandomPosition(minY, maxY)
            tries = 0
            while (Math.abs(_y - startY) > chipSize && tries < 20) {
                _y = clamp(getRandomPosition(minY, maxY), minY, maxY)
                tries++
            }
            _y = clamp(_y, minY, maxY)

            let _chipIndex = chips.value.value.indexOf(arg.amt)
            if (_chipIndex === -1) _chipIndex = 22

            chipsUI.value.push({
                x: _x,
                y: _y,
                amount: arg.amt,
                class: `_chip_${_chipIndex}`,
                type: arg.type
            })
            // if (!arg.audio) {
            // audioPlayer.Play(SOUNDKEY.BET_PLACE)
            // console.log('playSound')
            // }
        }

        const resetBetUI = () => {
            chipsUI.value = []
        }

        const getChipPositionStyle = (post: number): string => {
            const unitVar =
                deviceOrientation.value === 'landscape'
                    ? 'var(--pc1)'
                    : 'var(--mp1)'
            return `calc(${unitVar}*${post})`
        }

        const getRandomPosition = (min: number, max: number) => {
            return Math.floor(Math.random() * (max - min + 1) + min)
        }
        const chips = computed((): ChipOption => store.getters['chips'])
        const memberId = computed(() => store.getters['memberid'])
        const userCommandType = computed(() => store.getters['commandType'])
        const token = computed(() => store.getters['token'])
        const lang = computed(() => store.getters['lang'])

        const prevBets = computed(
            (): Dictionary<BetItem[]> => store.getters['prevBets']
        )

        const showCancelBet = computed((): boolean => {
            if (
                props.mData &&
                userCommandType.value === 2 &&
                props.mData.status === 1 &&
                props.confirmedBet > 0
            ) {
                return true
            }
            return false
        })
        const deviceOrientation = computed(
            (): 'landscape' | 'portrait' => store.getters['deviceOrientation']
        )


        const chipContainerStyle = computed((): StyleValue | undefined => {
            if (props.width > 0 && props.height > 0) {
                const w =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.width)
                        : Math.abs(props.mWidth)


                const h =
                    deviceOrientation.value === 'landscape'
                        ? Math.abs(props.height)
                        : Math.abs(props.mHeight)


                const _var =
                    deviceOrientation.value === 'landscape'
                        ? 'var(--pc1)'
                        : 'var(--mp1)'

                const _style = {
                    width: `calc(${_var}*${w})`,
                    height: `calc(${_var}*${h})`,
                    // backgroundColor: 'rgba(255, 255, 255, 0.3)'
                }


                return _style
            }
            return undefined
        })


        return {
            chipContainerStyle,
            betColor,
            sumOfTempBetandConfirmedBet,
            chipsUI,
            showCancelBet,
            lang,
            placeBet,
            toShortValue,
            handleOnPlaceChip,
            resetBetUI,
            removeTempUIBet,
            applyTempUIBet,
            cancelBet,
            getChipPositionStyle,
            t
        }
    }
})
